// The Dome home (/site). Real club films and photos (/media), no 3D. Every section has its own composition
// and its own motion idea; all motion is transform and opacity on the shared gsap ticker (Lenis drives it),
// except line drawing (stroke) for the intro mark, the map roads and the route. The official
// monogram (shared/brand) is placed, never redrawn; the intro traces its own outline before it fills.
// ?calm renders the complete static page: posters only, no autoplay, no scroll motion.
// Sources: shared motion.ts (Lenis, splitLines); jal-immersive scroll-choreography.md
// (one ticker, pin only at 768 and up, scrub with lag for calm motion); GreenSock docs (Flip, matchMedia).
import { gsap, ScrollTrigger, motionOn, splitLines, startLenis } from "../shared/motion";
import { Flip } from "gsap/Flip";
import { DrawSVGPlugin } from "gsap/DrawSVGPlugin";
import { mountChrome } from "../shared/chrome";
import { CourtPlan } from "../shared/plan";
import markSvg from "../shared/brand/the-dome-mark.svg" with { type: "text" };

gsap.registerPlugin(Flip, DrawSVGPlugin);
const motion = motionOn();
mountChrome({ active: "home" });

const $ = <T extends Element = HTMLElement>(s: string, r: ParentNode = document) => r.querySelector(s) as T;
const $$ = <T extends Element = HTMLElement>(s: string, r: ParentNode = document) => Array.from(r.querySelectorAll(s)) as T[];
const EASE = "expo.out";
const headerH = () => parseFloat(getComputedStyle(document.documentElement).getPropertyValue("--header-h")) || 64;
const smooth = (a: number, b: number, x: number) => {
  const t = Math.min(1, Math.max(0, (x - a) / (b - a)));
  return t * t * (3 - 2 * t);
};

// ---------- media: the club's own files are served at /media (outside the bundle), so URLs are set here ----------
function initMapMark() {
  // Both drawings of the map (wide and the phone crop) carry the real monogram in the pin.
  for (const host of $$("[data-map-mark]")) {
    const doc = new DOMParser().parseFromString(markSvg, "image/svg+xml");
    const svg = doc.documentElement;
    svg.querySelector("title")?.remove();
    svg.removeAttribute("role");
    svg.setAttribute("aria-hidden", "true");
    svg.setAttribute("x", "338"); svg.setAttribute("y", "186");
    svg.setAttribute("width", "20"); svg.setAttribute("height", "28");
    host.appendChild(document.importNode(svg, true));
  }
}

function initMedia() {
  initMapMark();
  // The real ring-and-dot monogram (never redrawn) marks the film, as on the club's own photographs.
  for (const el of $$("[data-brand-mark], [data-intro-mark]")) el.innerHTML = markSvg.replace(/<title>.*?<\/title>/, "").replace('role="img"', 'aria-hidden="true" focusable="false"');
  for (const v of $$<HTMLVideoElement>("video[data-poster]")) v.poster = v.dataset.poster!;
  for (const img of $$<HTMLImageElement>("img[data-img]")) img.src = img.dataset.img!;
}

// ---------- films: muted, looped, inline; play only while on screen; ?calm keeps the poster ----------
function playVideo(v: HTMLVideoElement) {
  if (!v.getAttribute("src") && v.dataset.src) {
    v.preload = "metadata";
    v.src = v.dataset.src;
  }
  v.play()?.catch(() => { /* poster stays */ });
}
function initVideos() {
  for (const v of $$<HTMLVideoElement>("video")) {
    v.muted = true;
    v.defaultMuted = true;
    v.loop = true;
    v.playsInline = true;
    v.disablePictureInPicture = true;
  }
  if (!motion) return;
  const vids = $$<HTMLVideoElement>("video[data-src]:not([data-manual])");
  const io = new IntersectionObserver((entries) => {
    for (const e of entries) {
      const v = e.target as HTMLVideoElement;
      if (e.isIntersecting) { v.autoplay = true; playVideo(v); }
      else if (!v.paused) v.pause();
    }
  }, { threshold: 0.2 });
  vids.forEach((v) => io.observe(v));
  document.addEventListener("visibilitychange", () => {
    if (document.hidden) vids.forEach((v) => v.pause());
  });
}

// ---------- 0. Page-load intro: the real mark traces its outline, fills, and the page settles ----------
function playIntro(): number {
  const ov = $<HTMLElement>("[data-intro-mark]");
  if (!motion || !ov) return 0;
  const paths = $$<SVGPathElement>("path", ov);
  const svg = $<SVGSVGElement>("svg", ov);
  gsap.set(paths, { drawSVG: "0%" });
  gsap.timeline({ onComplete: () => { ov.style.display = "none"; } })
    .fromTo(svg, { scale: 0.92, opacity: 0 }, { scale: 1, opacity: 1, duration: 0.5, ease: EASE }, 0)
    .to(paths, { drawSVG: "100%", duration: 1.1, ease: "power2.inOut", stagger: 0.08 }, 0)
    .to(paths, { attr: { "fill-opacity": 1 }, duration: 0.4, ease: "power1.out" }, 1.0)
    .to(svg, { scale: 0.86, opacity: 0, duration: 0.5, ease: "power2.in" }, 1.5)
    .to(ov, { opacity: 0, duration: 0.6, ease: "power2.out" }, 1.6);
  return 1.55;
}

// ---------- 1. Hero, the portal: the film opens from its centre, the two words slide in from the film's edges,
// and as the page moves the words part further while the film settles (they only ever move away from it) ----------
function initHero(delay: number) {
  if (!motion) return;
  const film = $$<HTMLElement>(".portal-film");
  gsap.timeline({ delay })
    .fromTo("[data-portal]", { clipPath: "inset(50% 50% 50% 50% round 28px)" }, { clipPath: "inset(0% 0% 0% 0% round 28px)", duration: 1.8, ease: EASE }, 0)
    .fromTo(film, { scale: 1.25 }, { scale: 1.06, duration: 2.4, ease: EASE }, 0)
    .fromTo("[data-hw] > span", { y: 0, yPercent: 110 }, { y: 0, yPercent: 0, duration: 1.4, ease: EASE, stagger: 0.14 }, 0.35)
    .fromTo("[data-intro]", { opacity: 0, y: 16 }, { opacity: 1, y: 0, duration: 1.2, ease: EASE }, 1.0);
  const mm = gsap.matchMedia();
  mm.add("(min-width: 1100px)", () => {
    const st = { trigger: ".hero", start: "top top", end: "bottom top", scrub: 1 };
    const tws = [
      gsap.fromTo("[data-hw='a']", { x: 0 }, { x: () => -innerWidth * 0.05, ease: "none", scrollTrigger: st }),
      gsap.fromTo("[data-hw='b']", { x: 0 }, { x: () => innerWidth * 0.05, ease: "none", scrollTrigger: st }),
      gsap.fromTo(film, { yPercent: 0 }, { yPercent: 2.5, ease: "none", immediateRender: false, scrollTrigger: st }),
    ];
    return () => tws.forEach((t) => { t.scrollTrigger?.kill(); t.kill(); });
  });
}

// ---------- 2. BLACK film band: pinned; the frames crossfade with slow Ken Burns, one line at a time ----------
function initStory() {
  const layers = $$<HTMLElement>(".story-layer");
  const lines = $$<HTMLElement>(".reel-line");
  if (!motion) return;
  const apply = (p: number) => {
    const f1 = smooth(0.26, 0.4, p);
    const f2 = smooth(0.6, 0.74, p);
    const w = [1 - f1, f1 * (1 - f2), f2];
    gsap.set(layers[0], { opacity: 1, scale: 1.04 + 0.14 * smooth(0, 0.4, p), xPercent: -2 * smooth(0, 0.4, p) });
    gsap.set(layers[1], { opacity: f1, scale: 1.16 - 0.08 * smooth(0.26, 0.74, p), xPercent: -1 - 2 * smooth(0.26, 0.74, p), transformOrigin: "85% 50%" });
    gsap.set(layers[2], { opacity: f2, scale: 1.14 - 0.14 * smooth(0.6, 1, p) });
    lines.forEach((l, i) => gsap.set(l, { opacity: w[i], y: 16 * (1 - w[i]) * (i === 0 ? -1 : 1) }));
  };
  apply(0);
  const state = { p: 0 };
  const mm = gsap.matchMedia();
  mm.add("(min-width: 768px) and (min-height: 600px)", () => {
    const tw = gsap.to(state, {
      p: 1, ease: "none", onUpdate: () => apply(state.p),
      scrollTrigger: { trigger: "[data-story-pin]", start: () => `top ${headerH() + 24}px`, end: "+=200%", pin: true, scrub: 1.2, anticipatePin: 1, invalidateOnRefresh: true },
    });
    // Hand-off: the band's frame scales up into place as it arrives from the hero.
    const enter = gsap.fromTo(".reel-frame", { scale: 0.9 }, { scale: 1, ease: "none", scrollTrigger: { trigger: ".reel", start: "top bottom", end: "top 20%", scrub: 1 } });
    return () => { tw.scrollTrigger?.kill(); tw.kill(); enter.scrollTrigger?.kill(); enter.kill(); };
  });
  mm.add("(max-width: 767px), (max-height: 599px)", () => {
    const tw = gsap.to(state, {
      p: 1, ease: "none", onUpdate: () => apply(state.p),
      scrollTrigger: { trigger: "[data-story-pin]", start: "top 70%", end: "bottom 30%", scrub: 1 },
    });
    return () => { tw.scrollTrigger?.kill(); tw.kill(); };
  });
}

// ---------- 3. Open today: live availability from /api/availability ----------
type Slot = { time: string; daypart: string; courts: { id: number; free: boolean }[]; courtsFree: number; price: number; peak: boolean };
const rupiah = (n: number) => "Rp " + Math.round(n).toLocaleString("id-ID");
const ymd = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const openState = { date: ymd(new Date()), duration: 60, ctrl: null as AbortController | null, note: "" };
let selectDay: (i: number) => void = () => {};

function initDays() {
  const host = $<HTMLElement>("[data-days]");
  const wd = new Intl.DateTimeFormat("en-GB", { weekday: "short" });
  const today = new Date();
  for (let i = 0; i < 7; i++) {
    const d = new Date(today.getFullYear(), today.getMonth(), today.getDate() + i);
    const b = document.createElement("button");
    b.type = "button";
    b.className = "day" + (i >= 5 ? " day-x" : "");
    b.dataset.date = ymd(d);
    b.setAttribute("aria-pressed", String(i === 0));
    b.setAttribute("aria-label", (i === 0 ? "Today, " : "") + d.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" }));
    b.innerHTML = `<span class="day-w">${i === 0 ? "Today" : wd.format(d)}</span><span class="day-n num">${d.getDate()}</span>`;
    host.appendChild(b);
  }
  const days = $$<HTMLButtonElement>(".day", host);
  const ring = document.createElement("span");
  ring.className = "day-ring";
  ring.setAttribute("aria-hidden", "true");
  days[0].appendChild(ring);
  const select = (d: HTMLButtonElement) => {
    const state = motion ? Flip.getState(ring) : null;
    days.forEach((x) => x.setAttribute("aria-pressed", String(x === d)));
    d.appendChild(ring);
    if (state) Flip.from(state, { duration: 0.6, ease: "power3.out" });
    openState.date = d.dataset.date!;
    loadSlots();
  };
  days.forEach((d) => d.addEventListener("click", () => select(d)));
  selectDay = (i) => select(days[Math.min(i, days.length - 1)]);
  const durs = $$<HTMLButtonElement>("[data-dur]");
  durs.forEach((b) => b.addEventListener("click", () => {
    durs.forEach((x) => x.setAttribute("aria-pressed", String(x === b)));
    openState.duration = Number(b.dataset.dur);
    loadSlots();
  }));
}

function skeleton(list: HTMLElement) {
  list.innerHTML = Array.from({ length: 5 }, () => `<li class="bk-skel" aria-hidden="true"><span class="sk sk-a"></span><span class="sk sk-btn"></span><span class="sk sk-b"></span></li>`).join("");
}

function stateBlock(list: HTMLElement, text: string, action: string, onAction: () => void) {
  list.innerHTML = `<li class="bk-state"><p class="subhead">${text}</p><button class="btn btn-secondary" type="button">${action}</button></li>`;
  list.querySelector("button")!.addEventListener("click", onAction);
}

/** The section head names the day shown: "Open today.", "Open tomorrow.", or the weekday ("Open Monday."). */
function setOpenHead(date: string) {
  const h = document.getElementById("op-h");
  if (!h) return;
  const now = new Date();
  const tmr = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
  const [y, m, d] = date.split("-").map(Number);
  const word = date === ymd(now) ? "today" : date === ymd(tmr) ? "tomorrow" : new Date(y, m - 1, d).toLocaleDateString("en-GB", { weekday: "long" });
  const text = `Open ${word}.`;
  if (h.hasAttribute("aria-label")) h.setAttribute("aria-label", text);
  // mid-reveal the text sits in one line mask: write into the innermost element
  let t: Element = h;
  while (t.children.length === 1) t = t.children[0];
  if (t.textContent !== text) t.textContent = text;
}

const dayLong = (date: string) => {
  const [y, m, d] = date.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" });
};

async function fetchFree(date: string, duration: number, signal: AbortSignal): Promise<Slot[]> {
  const res = await fetch(`/api/availability?date=${date}&duration=${duration}`, { signal, headers: { accept: "application/json" } });
  if (!res.ok) throw new Error(String(res.status));
  const data = (await res.json()) as { slots: Slot[] };
  const now = new Date();
  const isToday = date === ymd(now);
  const nowMin = now.getHours() * 60 + now.getMinutes();
  return (data.slots ?? []).filter((s) => {
    const [hh, mm] = s.time.split(":").map(Number);
    return s.courtsFree > 0 && (!isToday || hh * 60 + mm > nowMin);
  });
}

// The list always shows the day of the selected chip, headed by that day's name. On first load, when today's
// times have passed, the chip itself moves on to the first day with free times and the head says why.
let firstLoad = true;
async function loadSlots() {
  const box = $<HTMLElement>("[data-book]");
  const list = $<HTMLElement>("[data-book-list]");
  openState.ctrl?.abort();
  const ctrl = new AbortController();
  openState.ctrl = ctrl;
  box.setAttribute("aria-busy", "true");
  skeleton(list);
  lightCourts([], "");
  const { date, duration } = openState;
  const auto = firstLoad;
  firstLoad = false;
  const timer = setTimeout(() => ctrl.abort(), 8000);
  try {
    const slots = await fetchFree(date, duration, ctrl.signal);
    if (ctrl !== openState.ctrl) return;
    const days = $$<HTMLButtonElement>(".day");
    const idx = days.findIndex((d) => d.dataset.date === date);
    if (!slots.length && auto && idx === 0) {
      // Today is over: move the selection itself, so chip and list always agree.
      clearTimeout(timer);
      selectDay(1);
      return;
    }
    clearTimeout(timer);
    box.setAttribute("aria-busy", "false");
    setOpenHead(date);
    const note = openState.note;
    openState.note = "";
    if (!slots.length) {
      const next = days[idx + 1];
      const isToday = date === ymd(new Date());
      stateBlock(list, `${isToday ? "No times left today" : "No free times on " + dayLong(date)}.`, next ? `See ${dayLong(next.dataset.date!)}` : "See all times", () => {
        if (next) selectDay(idx + 1); else location.href = "/site/book";
      });
      return;
    }
    renderSlots(list, date, slots.slice(0, 6), duration, note);
  } catch (e) {
    clearTimeout(timer);
    if ((e as Error).name === "AbortError" && ctrl !== openState.ctrl) return;
    box.setAttribute("aria-busy", "false");
    stateBlock(list, "Times did not load.", "Try again", () => loadSlots());
  }
}

// The court-number plan (the one shared plan) lights the courts that are free at the time in focus.
let mapPlan: CourtPlan | null = null;
function lightCourts(free: number[], label: string) {
  mapPlan?.lights(Array.from({ length: 8 }, (_, i) => (free.includes(i + 1) ? 1 : 0)));
  mapPlan?.setLabel(free.length ? `The dome from above. ${label}: court${free.length > 1 ? "s" : ""} ${free.join(", ")} free.` : "The dome from above: eight courts in two rows, the mezzanine between them.");
  const cap = $<HTMLElement>("[data-map-cap]");
  cap.textContent = free.length
    ? `${label}: court${free.length > 1 ? "s" : ""} ${free.join(", ")} free. Entrance at the bottom.`
    : "Free courts light up for the time you choose. Entrance at the bottom.";
}

// Court names, short enough for one line on a phone: "1, 2, 5 +2" (the row's meta line truncates if it ever must).
function courtNames(free: number[]) {
  if (free.length === 1) return `Court ${free[0]}`;
  const more = free.length > 3 ? ` +${free.length - 3}` : "";
  return `${free.slice(0, 3).join(", ")}${more}`;
}

function renderSlots(list: HTMLElement, date: string, slots: Slot[], duration: number, note: string) {
  const day = dayLong(date);
  const head = `<li class="bk-day"><span class="caption">${day}</span>${note ? `<span class="body bk-day-note">${note}</span>` : ""}</li>`;
  list.innerHTML = head + slots.map((s) => {
    const free = s.courts.filter((c) => c.free).map((c) => c.id);
    const names = courtNames(free);
    const href = `/site/book?date=${date}&time=${encodeURIComponent(s.time)}&duration=${duration}`;
    return `<li><a class="bk-row" href="${href}" data-free="${free.join(",")}" data-time="${s.time}" aria-label="Book ${day}, ${s.time}, ${duration} minutes, ${s.courtsFree} court${s.courtsFree === 1 ? "" : "s"} free, ${rupiah(s.price)}">
      <span class="bk-time num">${s.time}</span>
      <span class="bk-meta num"><span class="bk-free">${s.courtsFree} court${s.courtsFree === 1 ? "" : "s"} free</span>${free.length ? `<span class="bk-dot" aria-hidden="true">·</span>${names}` : ""}</span>
      <span class="bk-price"><span class="num" data-price="${s.price}">${rupiah(s.price)}</span><svg class="ico" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M9 6l6 6-6 6"/></svg></span>
    </a></li>`;
  }).join("");
  const rows = $$<HTMLElement>(".bk-row", list);
  const focusRow = (r: HTMLElement) => {
    rows.forEach((x) => x.classList.toggle("is-current", x === r));
    lightCourts((r.dataset.free || "").split(",").filter(Boolean).map(Number), r.dataset.time || "");
  };
  rows.forEach((r) => { r.addEventListener("mouseenter", () => focusRow(r)); r.addEventListener("focusin", () => focusRow(r)); });
  if (rows[0]) focusRow(rows[0]);
  if (!motion) return;
  // Rows deal in from the left; prices count up to their value.
  gsap.fromTo(rows, { opacity: 0, x: -24 }, { opacity: 1, x: 0, duration: 0.9, ease: EASE, stagger: 0.07 });
  $$<HTMLElement>("[data-price]", list).forEach((el, i) => {
    const target = Number(el.dataset.price);
    const v = { n: Math.round((target * 0.55) / 1000) * 1000 };
    el.textContent = rupiah(v.n);
    gsap.to(v, { n: target, duration: 1.1, delay: 0.1 + i * 0.07, ease: "power3.out", onUpdate: () => { el.textContent = rupiah(Math.round(v.n / 1000) * 1000); } });
  });
}

function initOpen() {
  mapPlan = new CourtPlan($<HTMLCanvasElement>("[data-court-map]"), { motion, base: "surface" });
  if (motion) mapPlan.state.show.fill(0);
  initDays();
  loadSlots();
  if (!motion) return;
  // The plan's courts draw in one after another, in court-number order.
  ScrollTrigger.create({ trigger: ".op-map", start: "top 80%", once: true, onEnter: () => mapPlan?.intro({ courts: true }) });
}

// ---------- shared: real media settles into its frame as it arrives ----------
function settle(selector: string, trigger?: string) {
  $$<HTMLElement>(selector).forEach((m) => {
    const inner = m.querySelector("img, video");
    if (!inner) return;
    gsap.fromTo(inner, { scale: 1.16 }, { scale: 1, duration: 2, ease: EASE, scrollTrigger: { trigger: trigger ?? m, start: "top 88%", once: true } });
    gsap.fromTo(m, { opacity: 0, y: 32 }, { opacity: 1, y: 0, duration: 1.4, ease: EASE, scrollTrigger: { trigger: trigger ?? m, start: "top 88%", once: true } });
  });
}

// ---------- 3. IVORY details: the two frames rise at two speeds; the list draws in line by line ----------
function initFacilities() {
  if (!motion) return;
  settle(".dt-a-m", ".details");
  settle(".dt-b-m");
  gsap.from(".fac-list li", { opacity: 0, y: 16, duration: 1, ease: EASE, stagger: 0.06, scrollTrigger: { trigger: ".fac-list", start: "top 88%", once: true } });
  const mm = gsap.matchMedia();
  mm.add("(min-width: 768px)", () => {
    const a = gsap.fromTo(".dt-a-m img", { yPercent: -6, scale: 1.14 }, { yPercent: 6, scale: 1.14, ease: "none", scrollTrigger: { trigger: ".details", start: "top bottom", end: "bottom top", scrub: 1 } });
    // The film drifts inside its frame (the frames stay put, so the film and the list end on one line).
    const b = gsap.fromTo(".dt-b-m video", { yPercent: 6, scale: 1.14 }, { yPercent: -6, scale: 1.14, ease: "none", scrollTrigger: { trigger: ".details", start: "top bottom", end: "bottom top", scrub: 1.2 } });
    return () => { [a, b].forEach((t) => { t.scrollTrigger?.kill(); t.kill(); }); };
  });
}

// ---------- 5. Academy: today is ringed (selected state); the week cascades in ----------
function initAcademy() {
  const today = new Date().getDay();
  const row = $<HTMLElement>(`.ac-row[data-day="${today}"]`);
  if (row) {
    row.classList.add("is-today");
    const d = row.querySelector(".ac-d")!;
    d.textContent = "Today";
    row.setAttribute("aria-current", "date");
  }
  if (!motion) return;
  gsap.fromTo(".ac-row", { opacity: 0, y: 16 }, { opacity: 1, y: 0, duration: 1, ease: EASE, stagger: 0.08, scrollTrigger: { trigger: ".ac-week", start: "top 80%", once: true } });
  gsap.fromTo(".ac-media img", { yPercent: -6, scale: 1.14 }, { yPercent: 6, scale: 1.14, ease: "none", scrollTrigger: { trigger: ".ac-media", start: "top bottom", end: "bottom top", scrub: 1 } });
  gsap.fromTo(".ac-media", { opacity: 0 }, { opacity: 1, duration: 1.4, ease: EASE, scrollTrigger: { trigger: ".academy", start: "top 85%", once: true } });
}

// ---------- 6. NAVY events: three photographs at three depths ----------
function initEvents() {
  if (!motion) return;
  settle(".ev-collage .media", ".ev-collage");
  gsap.fromTo(".ev-copy > *", { opacity: 0, y: 24 }, { opacity: 1, y: 0, duration: 1.2, ease: EASE, stagger: 0.1, scrollTrigger: { trigger: ".ev-copy", start: "top 80%", once: true } });
  const st = { trigger: ".ev-collage", start: "top bottom", end: "bottom top", scrub: 1.2 };
  const a = gsap.fromTo(".ev-a img", { yPercent: -5, scale: 1.12 }, { yPercent: 5, scale: 1.12, ease: "none", scrollTrigger: { ...st } });
  const b = gsap.fromTo(".ev-b img", { yPercent: 9, scale: 1.2 }, { yPercent: -9, scale: 1.2, ease: "none", scrollTrigger: { ...st } });
  const c = gsap.fromTo(".ev-c img", { yPercent: -12, scale: 1.24 }, { yPercent: 12, scale: 1.24, ease: "none", scrollTrigger: { ...st } });
  void a; void b; void c;
}

// ---------- 7. IVORY ecosystem: the page holds while the tenants travel sideways, each frame drifting inside ----------
function initEco() {
  if (!motion) return;
  const viewport = $<HTMLElement>("[data-gallery-viewport]");
  const track = $<HTMLElement>("[data-gallery-track]");
  const items = $$<HTMLElement>(".eg-item");
  const mm = gsap.matchMedia();
  mm.add("(min-width: 1024px) and (min-height: 640px)", () => {
    document.documentElement.classList.add("gallery-on");
    // The pinned block fills the screen exactly: the frames take whatever height the head and captions leave.
    const pin = $<HTMLElement>("[data-gallery-pin]");
    const size = () => {
      const media = track.querySelector<HTMLElement>(".eg-media");
      if (!media) return;
      const rest = pin.offsetHeight - media.offsetHeight;
      track.style.setProperty("--g-h", `${Math.max(320, innerHeight - headerH() - 64 - rest)}px`);
    };
    size();
    ScrollTrigger.addEventListener("refreshInit", size);
    const dist = () => Math.max(0, track.scrollWidth - viewport.clientWidth);
    const tw = gsap.to(track, {
      x: () => -dist(), ease: "none",
      scrollTrigger: { trigger: "[data-gallery-pin]", start: () => `top ${headerH() + 32}px`, end: () => "+=" + dist(), pin: true, scrub: 1, anticipatePin: 1, invalidateOnRefresh: true },
    });
    galleryPinned = tw.scrollTrigger ?? null;
    const inner = items.map((it) => it.querySelector(".eg-media img, .eg-media video")).filter(Boolean).map((m) => gsap.fromTo(m, { xPercent: 8, scale: 1.18 }, {
      xPercent: -8, scale: 1.18, ease: "none",
      scrollTrigger: { trigger: (m as HTMLElement).closest(".eg-item"), containerAnimation: tw, start: "left right", end: "right left", scrub: true },
    }));
    return () => { ScrollTrigger.removeEventListener("refreshInit", size); track.style.removeProperty("--g-h"); tw.scrollTrigger?.kill(); tw.kill(); inner.forEach((t) => { t.scrollTrigger?.kill(); t.kill(); }); document.documentElement.classList.remove("gallery-on"); galleryPinned = null; gsap.set(track, { x: 0 }); };
  });
  mm.add("(max-width: 1023px), (max-height: 639px)", () => {
    const t = gsap.fromTo(items, { opacity: 0, x: 48 }, { opacity: 1, x: 0, duration: 1.2, ease: EASE, stagger: 0.08, scrollTrigger: { trigger: viewport, start: "top 85%", once: true } });
    return () => { t.scrollTrigger?.kill(); t.kill(); gsap.set(items, { clearProps: "opacity,transform" }); };
  });
}

// ---------- 7b. Gallery controls: one card per press; the rail never bleeds past the content margins ----------
let galleryPinned: ScrollTrigger | null = null;
function initGalleryControls() {
  const viewport = $<HTMLElement>("[data-gallery-viewport]");
  const track = $<HTMLElement>("[data-gallery-track]");
  const prev = $<HTMLButtonElement>("[data-eg-prev]");
  const next = $<HTMLButtonElement>("[data-eg-next]");
  if (!viewport || !prev || !next) return;
  const items = () => $$<HTMLElement>(".eg-item", track);
  const offsets = () => { const base = items()[0]?.offsetLeft ?? 0; return items().map((i) => i.offsetLeft - base); };
  const maxOffset = () => Math.max(0, track.scrollWidth - viewport.clientWidth);
  // Current offset of the rail: pinned mode moves the track, otherwise the viewport scrolls.
  const current = () => galleryPinned ? -(gsap.getProperty(track, "x") as number) : viewport.scrollLeft;
  const update = () => {
    const x = current(), max = maxOffset();
    prev.disabled = x <= 2;
    next.disabled = x >= max - 2;
  };
  const go = (dir: 1 | -1) => {
    const x = current(), max = maxOffset();
    const stops = [...offsets().filter((o) => o < max), max];
    const target = dir > 0 ? stops.find((o) => o > x + 2) ?? max : [...stops].reverse().find((o) => o < x - 2) ?? 0;
    const st = galleryPinned;
    if (st) {
      const y = st.start + (target / Math.max(1, max)) * (st.end - st.start);
      if (lenis) lenis.scrollTo(y, { duration: 1.1 }); else window.scrollTo({ top: y });
    } else {
      viewport.scrollTo({ left: target, behavior: motion ? "smooth" : "auto" });
    }
  };
  prev.addEventListener("click", () => go(-1));
  next.addEventListener("click", () => go(1));
  viewport.addEventListener("scroll", update, { passive: true });
  addEventListener("resize", update);
  if (motion) gsap.ticker.add(() => { if (galleryPinned) update(); });
  update();
}

// ---------- 8. Rules: each rule's hairline draws across and its number ticks up into place ----------
function initRules() {
  if (!motion) return;
  $$<HTMLElement>(".ru-row").forEach((row, i) => {
    gsap.timeline({ scrollTrigger: { trigger: row, start: "top bottom+=40", once: true }, delay: (i % 2) * 0.08 })
      .from($(".ru-line", row), { scaleX: 0, duration: 0.8, ease: "power3.inOut" })
      .from($(".ru-n-i", row), { yPercent: 110, duration: 0.8, ease: EASE }, 0.15)
      .from(row.querySelectorAll(".ru-t, .ru-sub"), { opacity: 0, y: 12, duration: 0.8, ease: EASE, stagger: 0.06 }, 0.2);
  });
}

// ---------- 9. Visit: the roads draw in, the route runs along the arterial road, the pin drops ----------
function initVisit() {
  if (!motion) return;
  const tl = gsap.timeline({ scrollTrigger: { trigger: ".vs-map", start: "top bottom", once: true } });
  tl.fromTo(".mp-road", { drawSVG: "0%" }, { drawSVG: "100%", duration: 0.9, ease: "power2.inOut", stagger: 0.06 })
    .from(".mp-street, .mp-poi", { opacity: 0, duration: 0.4, stagger: 0.02 }, 0.3)
    .from("[data-map-pin]", { y: -48, opacity: 0, duration: 0.8, ease: "back.out(1.6)", transformOrigin: "50% 100%" }, 0.8)
    .from(".mp-dome", { opacity: 0, x: -8, duration: 0.4, ease: EASE }, 1.1);
  gsap.fromTo(".vs-card > *", { opacity: 0, y: 24 }, { opacity: 1, y: 0, duration: 1.2, ease: EASE, stagger: 0.08, scrollTrigger: { trigger: ".vs-card", start: "top 85%", once: true } });
}

// ---------- magnetic primary actions (fine pointers only) ----------
function revealHeads() {
  if (!motion) return;
  for (const el of $$<HTMLElement>("[data-reveal]")) {
    const label = el.textContent ?? "";
    const lines = splitLines(el);
    lines.forEach((l) => l.setAttribute("aria-hidden", "true"));
    el.setAttribute("aria-label", label);
    gsap.from(lines, {
      yPercent: 105, duration: 1.1, ease: EASE, stagger: 0.1,
      scrollTrigger: { trigger: el, start: "top 98%", once: true },
      onComplete: () => { el.textContent = label; el.removeAttribute("aria-label"); },
    });
  }
}

function initMagnetic() {
  if (!motion || !matchMedia("(hover: hover) and (pointer: fine)").matches) return;
  for (const b of $$<HTMLElement>(".btn-primary")) {
    const xTo = gsap.quickTo(b, "x", { duration: 0.5, ease: "power3.out" });
    const yTo = gsap.quickTo(b, "y", { duration: 0.5, ease: "power3.out" });
    b.addEventListener("pointermove", (e) => {
      const r = b.getBoundingClientRect();
      xTo((e.clientX - (r.left + r.width / 2)) * 0.28);
      yTo((e.clientY - (r.top + r.height / 2)) * 0.4);
    });
    b.addEventListener("pointerleave", () => { gsap.to(b, { x: 0, y: 0, duration: 0.8, ease: "elastic.out(1, 0.45)", overwrite: "auto" }); });
  }
}

// ---------- boot ----------
initMedia();
initVideos();
initOpen();
initAcademy();
const lenis = startLenis();
initGalleryControls();
if (!motion) {
  ScrollTrigger.disable();
  gsap.ticker.sleep();
}
const fontsReady = Promise.race([
  Promise.all([
    document.fonts.load('300 57px "Inter"'),
    document.fonts.load('400 16px "Inter"'),
    document.fonts.load('600 16px "Inter"'),
  ]).then(() => document.fonts.ready),
  new Promise((r) => setTimeout(r, 1500)),
]).catch(() => undefined);
fontsReady.then(() => {
  if (!motion) return;
  initHero(playIntro());
  initMagnetic();
  initStory();
  revealHeads();
  initFacilities();
  initEco();
  initEvents();
  initRules();
  initVisit();
  ScrollTrigger.refresh();
});
addEventListener("load", () => { if (motion) ScrollTrigger.refresh(); });
